import React from 'react';

import MuiTable from '@mui/material/Table';
import {
  Box,
  Loader,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from 'components';
import { Danger, DirectInbox, TickCircle, Timer } from 'iconsax-react';

import { dayjs } from 'third-party';

import { APIPaymentEmailStatus } from 'types/api';

import { palette } from 'styles/theme';

type PaymentEmailStatusTableProps = {
  statuses: APIPaymentEmailStatus[];
  isLoading: boolean;
};

export const PaymentEmailStatusTable: React.FC<
  PaymentEmailStatusTableProps
> = ({ statuses, isLoading }) => {
  const getStatusIcon = (status: string | null) => {
    if (!status) return null;

    const statusLower = status.toLowerCase();

    if (statusLower.includes('delivered') || statusLower.includes('success')) {
      return (
        <Tooltip title={status}>
          <TickCircle variant="TwoTone" color={palette.primary900} size="20" />
        </Tooltip>
      );
    }

    if (
      statusLower.includes('failed') ||
      statusLower.includes('error') ||
      statusLower.includes('bounce')
    ) {
      return (
        <Tooltip title={status}>
          <Danger variant="TwoTone" color={palette.danger600} size="20" />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={status}>
        <Timer variant="TwoTone" color={palette.warning600} size="20" />
      </Tooltip>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  if (statuses.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        flexDirection="column"
        padding={5}
        alignItems="center"
        height="100%"
      >
        <DirectInbox
          variant="TwoTone"
          color={palette.primary900}
          size="4rem"
          style={{ marginBottom: '1rem', opacity: 0.9 }}
        />
        <Typography variant="h4">No email status data available</Typography>
      </Box>
    );
  }

  return (
    <Box padding={1}>
      <TableContainer component={Paper}>
        <MuiTable size="small" aria-label="email status table" width="30rem">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Sender</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Failure Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statuses.map((status, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getStatusIcon(status.deliveryStatus)}
                    {status.deliveryStatus || '-'}
                  </Box>
                </TableCell>
                <TableCell>{status.senderUsername || '-'}</TableCell>
                <TableCell>
                  {status.timeGenerated
                    ? dayjs(status.timeGenerated).format('YYYY-MM-DD HH:mm:ss')
                    : '-'}
                </TableCell>
                <TableCell>
                  {status.failureReason ? (
                    <Tooltip title={status.failureMessage || ''}>
                      <span>{status.failureReason}</span>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};
